/**
 * ⟦C57 · A-174⟧ **L'arbre en abrégé** : ce que la carte d'un nœud garde à l'écran, et ce qui part
 * dans « Les dessous ».
 *
 * La carte d'un nœud montre son nom, son palier, UNE ligne d'effet (celle de `lecture.effets[0]`,
 * si elle tient dans la règle : 12 mots au plus, `game/dessous.ts`), les pictogrammes des
 * mini-jeux qu'il assiste, son coût et le bouton. Tout le reste (la description littéraire, les
 * autres effets, les lignes d'assist, les contre-mesures, la porte d'abonnés, les prérequis)
 * s'ouvre dans « Les dessous » du nœud. **Rien n'est supprimé, rien n'est recalculé** : ce module
 * ne fait que choisir quelles lignes de `lecture.ts` restent sur la carte.
 */

import { estUnePhraseCourte } from "../../game/dessous";

import type { LectureNoeud } from "./lecture";

/**
 * La ligne d'effet gardée sur la carte : l'effet principal, s'il est une phrase courte. Un effet
 * long (« la direction : un freelance parcourt 40 % du chemin vers… ») n'a pas de forme courte
 * dans le contenu : il ne s'affiche pas sur la carte, il reste dans les dessous.
 */
export function effetPrincipalCourt(lecture: LectureNoeud): string | null {
  const principal = lecture.effets[0];
  return principal !== undefined && estUnePhraseCourte(principal) ? principal : null;
}

/**
 * ⟦C62 · lot arbre · A-179⟧ **La ligne d'effet de la carte** : la phrase de joueur écrite dans le contenu
 * (`effet_court`, 12 mots au plus), sinon l'effet principal d'avant (`effetPrincipalCourt`). Un contenu qui
 * ne la porte pas se lit comme avant ce chantier.
 */
export function texteDeCarte(noeud: { effet_court?: string }, lecture: LectureNoeud): string | null {
  return noeud.effet_court ?? effetPrincipalCourt(lecture);
}

/**
 * ⟦C62 · lot arbre · A-179⟧ **Le détail ne redit pas la carte** : parmi les lignes d'effets, on retire celle que la
 * carte écrit déjà mot pour mot (cas du repli sur l'effet principal : sans cela, « +0,05 de qualité » serait écrit
 * sur la carte et répété dans ses dessous).
 */
export function effetsSansLaCarte(lecture: LectureNoeud, carte: string | null): string[] {
  return lecture.effets.filter((e) => e !== carte);
}

/**
 * ⟦C62 · lot arbre⟧ Les assists, une ligne PAR mini-jeu (un nœud qui en assiste un de deux façons n'écrit pas deux
 * fois son nom) : « 🎥 La prise parfaite : zone verte +10 % · plateau éclairé : la zone verte s'élargit ».
 */
export function lignesDAssists(lecture: LectureNoeud): { icone: string; texte: string }[] {
  const parJeu = new Map<string, { icone: string; textes: string[] }>();
  for (const a of lecture.assists) {
    const ligne = parJeu.get(a.jeu) ?? { icone: a.icone, textes: [] };
    ligne.textes.push(a.texte);
    parJeu.set(a.jeu, ligne);
  }
  return [...parJeu].map(([jeu, { icone, textes }]) => ({ icone, texte: `${jeu} : ${textes.join(" · ")}` }));
}

/** Les pictogrammes des mini-jeux assistés, sans doublon, dans l'ordre : un nombre, une icône, pas une phrase. */
export function iconesDAssists(lecture: LectureNoeud): string[] {
  return [...new Set(lecture.assists.map((a) => a.icone))];
}
